import { CHIPS, SORT_ORDERS, type Chip, type SortOrder } from '../core/triage';

const RATES = [0.25, 0.5, 1, 2, 4, 8, 16] as const;

export type Direction = 1 | -1;

export type Command =
  | { readonly kind: 'toggle-play' }
  | { readonly kind: 'seek-by'; readonly seconds: number }
  | { readonly kind: 'step-frame'; readonly direction: Direction }
  | { readonly kind: 'rate-step'; readonly direction: Direction }
  | { readonly kind: 'rate'; readonly rate: number }
  | { readonly kind: 'toggle-autoplay' }
  | { readonly kind: 'fullscreen' }
  | { readonly kind: 'marker'; readonly direction: Direction }
  | { readonly kind: 'event'; readonly direction: Direction }
  | { readonly kind: 'toggle-session' }
  | { readonly kind: 'delete' }
  | { readonly kind: 'map' }
  | { readonly kind: 'ai' }
  | { readonly kind: 'export' }
  | { readonly kind: 'focus'; readonly index: number }
  | { readonly kind: 'unfocus' }
  | { readonly kind: 'palette' }
  | { readonly kind: 'search' }
  | { readonly kind: 'open-folder' }
  | { readonly kind: 'chip'; readonly chip: Chip }
  | { readonly kind: 'sort'; readonly order: SortOrder }
  | { readonly kind: 'sort-cycle' }
  | { readonly kind: 'help' };

export type CommandGroup = 'Playback' | 'Moments' | 'Events' | 'Cameras' | 'Library';

export interface KeyBinding {
  readonly key?: string;
  readonly code?: string;
  readonly mod?: boolean;
}

export interface CommandEntry {
  readonly command: Command;
  readonly title: string;
  readonly group: CommandGroup;
  readonly keys: readonly string[];
  readonly bindings: readonly KeyBinding[];
}

export interface KeyPress {
  readonly key: string;
  readonly code: string;
  readonly altKey: boolean;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly shiftKey: boolean;
}

const JUMP_S = 5;
const CAMERA_SLOTS = 6;
const RATE_SHORTCUTS: Readonly<Record<number, number>> = { 0.5: 0, 1: 1, 2: 2, 4: 3, 8: 4, 16: 5 };
const CHIP_TITLES: Readonly<Record<Chip, string>> = {
  all: 'Show all events',
  incidents: 'Show incidents',
  driving: 'Show events with driving moments',
  saved: 'Show saved events',
  sentry: 'Show Sentry events',
  clips: 'Show plain clips',
};

const entry = (command: Command, title: string, group: CommandGroup, keys: readonly string[] = [], bindings: readonly (string | KeyBinding)[] = []): CommandEntry => ({
  command,
  title,
  group,
  keys,
  bindings: bindings.map((binding) => (typeof binding === 'string' ? { key: binding } : binding)),
});

export const SORT_TITLES: Readonly<Record<SortOrder, string>> = {
  rank: 'Most important',
  newest: 'Newest',
  oldest: 'Oldest',
  type: 'Event type',
};

const rateLabel = (rate: number): string => (rate === 0.25 ? '¼' : rate === 0.5 ? '½' : String(rate));

export const COMMANDS: readonly CommandEntry[] = [
  entry({ kind: 'toggle-play' }, 'Play or pause', 'Playback', ['Space'], [' ', 'k']),
  entry({ kind: 'seek-by', seconds: -JUMP_S }, 'Back 5 seconds', 'Playback', ['←'], ['ArrowLeft', 'j']),
  entry({ kind: 'seek-by', seconds: JUMP_S }, 'Forward 5 seconds', 'Playback', ['→'], ['ArrowRight', 'l']),
  entry({ kind: 'step-frame', direction: -1 }, 'Previous frame', 'Playback', [','], [',']),
  entry({ kind: 'step-frame', direction: 1 }, 'Next frame', 'Playback', ['.'], ['.']),
  entry({ kind: 'rate-step', direction: -1 }, 'Slower', 'Playback', ['<'], ['<']),
  entry({ kind: 'rate-step', direction: 1 }, 'Faster', 'Playback', ['>'], ['>']),
  ...RATES.map((rate) => {
    const digit = RATE_SHORTCUTS[rate];
    return digit === undefined
      ? entry({ kind: 'rate', rate }, `Play at ${rateLabel(rate)}× speed`, 'Playback')
      : entry({ kind: 'rate', rate }, `Play at ${rateLabel(rate)}× speed`, 'Playback', ['⇧', String(digit)], [{ code: `Digit${digit}` }]);
  }),
  entry({ kind: 'toggle-autoplay' }, 'Turn autoplay on or off', 'Playback', ['A'], ['a']),
  entry({ kind: 'fullscreen' }, 'Fullscreen', 'Playback', ['F'], ['f']),
  entry({ kind: 'marker', direction: -1 }, 'Previous moment', 'Moments', ['['], ['[']),
  entry({ kind: 'marker', direction: 1 }, 'Next moment', 'Moments', [']'], [']']),
  entry({ kind: 'event', direction: -1 }, 'Previous event', 'Events', ['↑'], ['ArrowUp', 'p']),
  entry({ kind: 'event', direction: 1 }, 'Next event', 'Events', ['↓'], ['ArrowDown', 'n']),
  entry({ kind: 'toggle-session' }, 'Expand or collapse the parking session', 'Events', ['X'], ['x']),
  entry({ kind: 'delete' }, 'Delete this recording', 'Events', ['⌫'], ['Backspace', 'Delete']),
  entry({ kind: 'map' }, 'Open the place on a map', 'Events', ['M'], ['m']),
  entry({ kind: 'ai' }, 'Show AI notes and instructions', 'Events', ['I'], ['i']),
  entry({ kind: 'export' }, 'Export a video', 'Events', ['E'], ['e']),
  ...Array.from({ length: CAMERA_SLOTS }, (_, index) => entry({ kind: 'focus', index }, `Focus angle ${index + 1}`, 'Cameras', [String(index + 1)], [String(index + 1)])),
  entry({ kind: 'unfocus' }, 'Show all angles', 'Cameras', ['0'], ['0', 'Escape']),
  entry({ kind: 'palette' }, 'Search commands', 'Library', ['⌘', 'K'], [{ key: 'k', mod: true }]),
  entry({ kind: 'search' }, 'Search events', 'Library', ['/'], ['/']),
  entry({ kind: 'open-folder' }, 'Open a folder', 'Library', ['O'], ['o']),
  ...CHIPS.map((chip) => entry({ kind: 'chip', chip }, CHIP_TITLES[chip], 'Library')),
  entry({ kind: 'sort-cycle' }, 'Change the sort order', 'Library', ['S'], ['s']),
  ...SORT_ORDERS.map((order) => entry({ kind: 'sort', order }, `Sort by ${SORT_TITLES[order].toLowerCase()}`, 'Library')),
  entry({ kind: 'help' }, 'Keyboard shortcuts', 'Library', ['?'], ['?']),
];

const normalize = (key: string): string => (key.length === 1 ? key.toLowerCase() : key);

const matches = (binding: KeyBinding, press: KeyPress, key: string, mod: boolean): boolean =>
  Boolean(binding.mod) === mod && (binding.code === undefined ? binding.key === key : press.shiftKey && binding.code === press.code);

export function commandFor(press: KeyPress): Command | undefined {
  if (press.altKey) return undefined;
  const mod = press.metaKey || press.ctrlKey;
  const key = normalize(press.key);
  return COMMANDS.find((candidate) => candidate.bindings.some((binding) => matches(binding, press, key, mod)))?.command;
}

export function searchCommands(query: string, available: (command: Command) => boolean): readonly CommandEntry[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  return COMMANDS.filter((candidate) => available(candidate.command) && words.every((word) => `${candidate.title} ${candidate.group}`.toLowerCase().includes(word)));
}

export function nextRate(rate: number, direction: Direction): number {
  const index = RATES.findIndex((candidate) => candidate >= rate);
  const current = index < 0 ? RATES.length - 1 : index;
  return RATES[Math.min(RATES.length - 1, Math.max(0, current + direction))]!;
}

export const shortcutCommands = (): readonly CommandEntry[] => COMMANDS.filter((candidate) => candidate.keys.length > 0);
